import { useState, useEffect, useCallback, useMemo, useRef, type ReactElement } from 'react'
import i18n from 'i18n'
import { throttle } from 'lodash'

// Map imports
import {
  Map,
  type MapRef,
  FullscreenControl,
  NavigationControl,
  ScaleControl,
  Source,
  Layer,
  AttributionControl,
  type MapLayerMouseEvent,
  type MapSourceDataEvent,
  type StyleSpecification,
} from 'react-map-gl/maplibre'
import {
  GeoJSONSource,
  type MapGeoJSONFeature,
} from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import './maplibre.css'
import { circle } from '@turf/circle'
import { polygon, point, lineString, featureCollection } from '@turf/helpers'
import { nearestPoint } from '@turf/nearest-point'
import { bbox } from '@turf/bbox'
import type { Point, Polygon, Feature, FeatureCollection, GeoJsonProperties } from 'geojson'

import Typography from '@mui/material/Typography'

// KN imports
import { relativeDate } from 'global/helpers/dates'
import { getEstimatedSpeedLabel, getRouteColor } from 'screens/TripDetails/TripDetails.helpers'
import KNMapLibreMarker from './KNMapLibreMarker'
import KNMapLibreLayersControl from './KNMapLibreLayersControl'
import {
  type KNMapLibreProps,
  type MapMarker,
  MapLayerType,
  type MapLayers,
} from './types'
import { getMarkerTypeColor, getHeadingDate, processMapStyle } from './helpers'
import {
  getMetadataLayer,
  getRoutesLayer,
  clustersLayer,
  clustersLabelLayer,
  geofencesLayer,
  geofencesOutlineLayer,
} from './layers'
import style from './style.json'
import KNMapLibreTooltip from './KNMapLibreTooltip'
import KNMapLibreSourceSelector from './KNMapLibreSourceSelector'
import KNMapLibreCircleSelector from './KNMapLibreCircleSelector'

const getHeadingTooltip = (properties: GeoJsonProperties): ReactElement | null => {
  if (!properties) {
    return null
  }
  const estimatedSpeedLabel = getEstimatedSpeedLabel(properties['estimatedSpeed'])
  return (
    <>
      <Typography variant="p3">{relativeDate(properties['lastTimestamp'] || properties['timestamp'])}</Typography>
      <Typography component="p" variant="p5">{getHeadingDate(properties['timestamp'], properties['lastTimestamp'])}</Typography>
      {(properties['estimatedSpeed'] ?? 0) > 0 && (
        <Typography component="p" variant="p5"
          color="primary.light">{i18n.t(`screens.cs.trip_details.map.${estimatedSpeedLabel}`)}</Typography>
      )}
    </>
  )
}

const KNMapLibre = ({
  markers = [],
  geoPoints,
  groupedGeoPoints,
  shortestRoutes,
  focusGeoPoint,
  withHeading = false,
  withoutLayersControl = false,
  onMarkerClick,
  withSelectors = false,
  selectionSource,
  selectionMode,
  selections = [],
  onSelectionChange,
  customAttribution,
}: KNMapLibreProps): ReactElement | null => {
  const [mapStyle, setMapStyle] = useState<StyleSpecification>()
  const [markersMetadata, setMarkersMetadata] = useState<FeatureCollection>()
  const [vehiclesMetadata, setVehiclesMetadata] = useState<FeatureCollection>()
  const [routesMetadata, setRoutesMetadata] = useState<FeatureCollection>()
  const mapRef = useRef<MapRef | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [routes, setRoutes] = useState<FeatureCollection>()
  const [geofences, setGeofences] = useState<FeatureCollection>()
  const [activeMarkers, setActiveMarkers] = useState<MapMarker[]>([])
  const [visibleMarkers, setVisibleMarkers] = useState<MapMarker[]>([])
  const [headingMarker, setHeadingMarker] = useState<MapMarker>()
  const [tooltipMarkers, setTooltipMarkers] = useState<MapMarker[]>([])
  const [activeLayers, setActiveLayers] = useState<MapLayers>({
    [MapLayerType.TRACKED_ROUTES]: true,
    [MapLayerType.SHORTEST_ROUTES]: true,
    [MapLayerType.PARKING_LOCATIONS]: true,
  })

  const updateVisibleMarkers = useCallback(() => {
    if (!mapRef.current) {
      return
    }

    if (!mapRef.current.getLayer('markers_metadata')) {
      return
    }

    const features: MapGeoJSONFeature[] = mapRef.current.queryRenderedFeatures(
      {
        layers: ['markers_metadata'],
      },
    )

    const visibleMarkerFeatures = features.filter((feature) => feature.properties['class'] === 'marker')
    const visibleMarkerIds = visibleMarkerFeatures.map((feature) => feature.properties?.['id'])
    setVisibleMarkers(activeMarkers.filter((marker) => visibleMarkerIds.includes(marker.id) || marker.type === 'VEHICLE'))
  }, [mapRef, markersMetadata, activeMarkers])

  useEffect(() => {
    setMapStyle(processMapStyle(style as StyleSpecification))
  }, [])

  useEffect(() => {
    if (markers.length === 0) {
      return
    }

    if (!activeLayers[MapLayerType.PARKING_LOCATIONS]) {
      setActiveMarkers(markers.filter((marker) => marker.type !== 'PARKING_LOCATION'))
    } else {
      setActiveMarkers(markers)
    }
  }, [markers, activeLayers])

  useEffect(() => {
    if (markers.length === 0) {
      return
    }

    const markersMetadataCollection = featureCollection(
      activeMarkers.filter((marker) => marker.type !== 'VEHICLE').map((marker) => point(
        [
          marker.longitude,
          marker.latitude,
        ], {
          class: 'marker',
          id: marker.id,
          type: marker.type,
        } as GeoJsonProperties,
      )),
    )

    setMarkersMetadata(markersMetadataCollection)

    const vehiclesMetadataCollection = featureCollection(
      activeMarkers.filter((marker) => marker.type === 'VEHICLE').map((marker) => point(
        [
          marker.longitude,
          marker.latitude,
        ], {
          class: 'marker',
          id: marker.id,
          type: marker.type,
        } as GeoJsonProperties,
      )),
    )

    setVehiclesMetadata(vehiclesMetadataCollection)

    const routesMetadataCollection = featureCollection(
      geoPoints?.map((geoPoint) => point(
        [
          geoPoint.longitude,
          geoPoint.latitude,
        ],
        {
          class: 'route',
          ...geoPoint,
        } as GeoJsonProperties,
      )) ?? [],
    )

    setRoutesMetadata(routesMetadataCollection)

    const routesFeature = groupedGeoPoints?.filter((group) => group.geoPoints.length > 1).map((group) =>
      lineString(
        group.geoPoints.map((geoPoint) => [geoPoint.longitude, geoPoint.latitude]),
        {
          class: group.label,
          color: getRouteColor(group.label),
        } as GeoJsonProperties,
      ),
    ) ?? []

    const shortestRoutesFeature = shortestRoutes?.filter((route) => route.length > 1).map((route) =>
      lineString(
        route.map((geoPoint) => [geoPoint.longitude, geoPoint.latitude]),
        {
          class: 'shortest_route',
          color: getRouteColor('shortest_route'),
        } as GeoJsonProperties,
      ),
    ) ?? []

    setRoutes(featureCollection([
      ...(activeLayers[MapLayerType.TRACKED_ROUTES] ? routesFeature : []),
      ...(activeLayers[MapLayerType.SHORTEST_ROUTES] && shortestRoutesFeature[0] ? [shortestRoutesFeature[0]] : []),
    ].filter((featureCollection) => featureCollection)))
  }, [mapRef, markers, activeMarkers, activeLayers])

  useEffect(() => {
    // show geofences only for unclustered markers
    const geofencesFeatureCollection = featureCollection(
      visibleMarkers.map((marker) => {
        const properties = {
          id: marker.id,
          class: 'geofence',
          color: marker.color ?? getMarkerTypeColor(marker.type),
        }
        if (marker.geofencePolygon) {
          return polygon(
            [marker.geofencePolygon.map((geoPoint) => [geoPoint.longitude, geoPoint.latitude])],
            properties,
          )
        }
        if (marker.geofenceRadius) {
          return circle(
            [
              marker.longitude,
              marker.latitude,
            ],
            marker.geofenceRadius / 1000,
            {
              steps: 64,
              properties,
            },
          )
        }
        return null
      }).filter((polygon) => polygon) as Feature<Polygon, GeoJsonProperties>[],
    )

    setGeofences(geofencesFeatureCollection)
  }, [visibleMarkers])

  const handleLoad = useCallback(() => {
    if (!mapRef.current) {
      return
    }

    if (focusGeoPoint) {
      mapRef.current.flyTo({
        center: [focusGeoPoint.longitude, focusGeoPoint.latitude],
        zoom: 12,
        speed: 2,
      })
    } else if (markersMetadata) {
      const boundingBox = bbox(markersMetadata)
      // use jumpTo instead of fitBounds if bounding box is just a single point
      if (boundingBox[0] === boundingBox[2] && boundingBox[1] === boundingBox[3]) {
        mapRef.current.jumpTo({
          center: [boundingBox[0], boundingBox[1]],
          zoom: 12,
        })
      } else {
        mapRef.current.fitBounds(
          [[boundingBox[0], boundingBox[1]], [boundingBox[2], boundingBox[3]]],
          {
            animate: false,
            maxZoom: 12,
            padding: 64,
          },
        )
      }
    } else {
      mapRef.current.jumpTo({
        center: [9.991439, 53.5410679],
        zoom: 4,
      })
    }

    setMapLoaded(true)
  }, [mapRef, markersMetadata, focusGeoPoint])

  useEffect(() => {
    handleLoad()
  }, [mapRef, markersMetadata, focusGeoPoint])

  const handleMouseMove = useCallback(async (event: MapLayerMouseEvent) => {
    if (!event.target.getLayer('markers_metadata')) {
      return
    }

    // get all features inside a bbox around the mouse pointer
    const THRESHOLD = 18
    const features: MapGeoJSONFeature[] = event.target.queryRenderedFeatures(
      [
        [event.point.x - THRESHOLD, event.point.y - THRESHOLD],
        [event.point.x + THRESHOLD, event.point.y + THRESHOLD],
      ],
      {
        layers: ['markers_metadata', 'vehicles_metadata', 'routes_metadata'],
      },
    )

    // get clustered markers
    const clusterFeatures = features.filter((feature) => feature.properties['cluster'])
    const clusteredMarkerIds = await clusterFeatures.reduce(async (idsPromise, cluster) => {
      const ids = await idsPromise
      const source = mapRef.current!.getSource(cluster.source) as GeoJSONSource
      const clusteredMarkers = await source.getClusterLeaves(cluster.properties['cluster_id'], cluster.properties['point_count'], 0)
      return ids.concat(clusteredMarkers.map((feature) => feature.properties?.['id']))
    }, Promise.resolve([] as string[]))
    const clusteredMarkers = activeMarkers.filter((marker) => clusteredMarkerIds.includes(marker.id)).map((marker) => {
      marker.grouped = marker.groupable
      return marker
    })

    // get unclustered markers
    const markerFeatures = features.filter((feature) => feature.properties['class'] === 'marker')
    const unclusteredMarkerIds = markerFeatures.map((feature) => feature.properties?.['id'])
    const unclusteredMarkers = activeMarkers.filter((marker) => unclusteredMarkerIds.includes(marker.id)).map((marker) => {
      marker.grouped = false
      return marker
    })

    // prepare markers list for tooltip
    const tooltipMarkers = [
      ...unclusteredMarkers,
      ...clusteredMarkers,
    ]

    // add heading marker if near a route
    if (withHeading) {
      const routeFeatures = features.filter((feature) => feature.properties['class'] === 'route')
      if (routeFeatures.length > 0) {
        const nearest = nearestPoint([event.lngLat.lng, event.lngLat.lat], featureCollection(routeFeatures as Feature<Point, GeoJsonProperties>[]))
        const headingMarker: MapMarker = {
          id: 'heading',
          latitude: nearest.geometry.coordinates[1]!,
          longitude: nearest.geometry.coordinates[0]!,
          type: 'HEADING',
          color: getRouteColor(getEstimatedSpeedLabel(nearest.properties['estimatedSpeed'])),
          heading: nearest.properties['heading'],
          tooltip: getHeadingTooltip(nearest.properties) ?? undefined,
        }
        // add heading tooltip at the top of the stack
        tooltipMarkers.unshift(headingMarker)
        setHeadingMarker(headingMarker)
      } else {
        setHeadingMarker(undefined)
      }
    }

    setTooltipMarkers(tooltipMarkers)
  }, [activeMarkers])
  const handleMouseMoveThrottled = useMemo(
    () => throttle(handleMouseMove, 66, { leading: true, trailing: false }),
    [handleMouseMove, activeMarkers],
  )

  const handleViewStateChange = useCallback(() => {
    updateVisibleMarkers()
  }, [mapRef, markersMetadata, activeMarkers])
  const handleViewStateChangeThrottled = useMemo(
    () => throttle(handleViewStateChange, 200, { leading: true, trailing: false }),
    [handleViewStateChange, mapRef, markersMetadata, activeMarkers],
  )

  const handleMapSourceDataEvent = useCallback((event: MapSourceDataEvent) => {
    if (event.isSourceLoaded && event.sourceId === 'markers_metadata') {
      updateVisibleMarkers()
    }
  }, [mapRef, markersMetadata, activeMarkers])

  const handleLayerToggle = useCallback((layer: MapLayerType) => {
    setActiveLayers({
      ...activeLayers,
      [layer]: !activeLayers[layer],
    })
  }, [activeLayers])

  if (!mapStyle) {
    return null
  }

  return (
    <Map
      ref={mapRef}
      mapStyle={mapStyle}
      onLoad={handleLoad}
      onMouseMove={handleMouseMoveThrottled}
      onZoom={handleViewStateChangeThrottled}
      onZoomEnd={handleViewStateChangeThrottled}
      onMove={handleViewStateChangeThrottled}
      onMoveEnd={handleViewStateChangeThrottled}
      onDrag={handleViewStateChangeThrottled}
      onDragEnd={handleViewStateChangeThrottled}
      onSourceData={handleMapSourceDataEvent}
      dragRotate={false}
      minZoom={0}
      maxZoom={18}
      attributionControl={false}
      style={{
        // avoids the flash of the map at 0,0 coordinates before it's centered on a point or a bounding box
        opacity: mapLoaded ? 1 : 0,
      }}
    >
      <AttributionControl compact customAttribution={customAttribution} />
      <FullscreenControl position="top-left" />
      <NavigationControl position="top-left" showCompass={false} />
      <ScaleControl />
      {!withoutLayersControl && <KNMapLibreLayersControl position="top-left" activeLayers={activeLayers} onClick={handleLayerToggle} />}

      {withSelectors && onSelectionChange && selectionSource && (
        <KNMapLibreSourceSelector source={selectionSource} mode={selectionMode} selections={selections} onChange={onSelectionChange} />
      )}
      {withSelectors && onSelectionChange && (
        <KNMapLibreCircleSelector mode={selectionMode} selections={selections} onChange={onSelectionChange} />
      )}

      {markersMetadata && (
        <Source id="markers_metadata" type="geojson" data={markersMetadata} cluster clusterMaxZoom={14} clusterRadius={32}>
          <Layer {...getMetadataLayer('markers_metadata')} />
          <Layer {...clustersLayer} />
          <Layer {...clustersLabelLayer} />
        </Source>
      )}

      {vehiclesMetadata && (
        <Source id="vehicles_metadata" type="geojson" data={vehiclesMetadata}>
          <Layer {...getMetadataLayer('vehicles_metadata')} />
        </Source>
      )}

      {routesMetadata && (
        <Source id="routes_metadata" type="geojson" data={routesMetadata}>
          <Layer {...getMetadataLayer('routes_metadata')} />
        </Source>
      )}

      {geofences && (
        <Source id="geofences" type="geojson" data={geofences}>
          <Layer beforeId="label-other" {...geofencesOutlineLayer} />
          <Layer beforeId="label-other" {...geofencesLayer} />
        </Source>
      )}

      {routes && (
        <Source id="routes" type="geojson" data={routes}>
          <Layer beforeId="label-other" {...getRoutesLayer('shortest_route')} />
          <Layer beforeId="label-other" {...getRoutesLayer('vehicle_fast')} />
          <Layer beforeId="label-other" {...getRoutesLayer('vehicle_medium')} />
          <Layer beforeId="label-other" {...getRoutesLayer('vehicle_slow')} />
          <Layer beforeId="label-other" {...getRoutesLayer('interruption')} />
        </Source>
      )}

      {headingMarker && (
        <KNMapLibreMarker key={headingMarker.id} marker={headingMarker} />
      )}

      {visibleMarkers.map((marker) => (
        <KNMapLibreMarker key={marker.id} marker={marker} onMarkerClick={onMarkerClick} />
      ))}

      {tooltipMarkers.length > 0 && (
        <KNMapLibreTooltip markers={tooltipMarkers} />
      )}
    </Map>
  )
}

export default KNMapLibre
